/**
 * Redacción del mensaje del usuario por aviso del backend (card BDe1FCcc).
 *
 * Con el flujo Duppla activo, chat-service intercepta un número de documento
 * escrito en el chat (card DOb6M7IT): no lo manda al LLM ni lo guarda, y en su
 * historial queda el texto saneado. Para que el navegador tampoco lo guarde,
 * el stream `/chat/stream/v2` manda, antes que cualquier otro contenido del
 * turno, un chunk transient:
 *
 *     {"type":"data-user-message-redacted","transient":true,"data":{"text":"…"}}
 *
 * `data.text` es el texto saneado que guardó el servidor (la marca de mensaje
 * rechazado, o el mensaje con `[documento omitido]` en lugar de cada número).
 *
 * El cliente NO detecta documentos: la regla vive sólo en el backend
 * (`duppla_flow.revisar_documento_en_chat`). Acá sólo se aplica el texto que
 * llega.
 */

import type { UIMessage } from 'ai';

/** Tipo del chunk `data-*` que avisa la redacción. Emisor: `block_emitter.py`. */
export const USER_MESSAGE_REDACTED_PART = 'data-user-message-redacted';

/**
 * Devuelve el texto saneado si `dataPart` es el aviso de redacción con un
 * `data.text` no vacío; `null` en cualquier otro caso.
 */
export function parseUserMessageRedacted(dataPart: unknown): string | null {
  if (!dataPart || typeof dataPart !== 'object') return null;
  const part = dataPart as { type?: unknown; data?: unknown };
  if (part.type !== USER_MESSAGE_REDACTED_PART) return null;
  if (!part.data || typeof part.data !== 'object') return null;
  const text = (part.data as { text?: unknown }).text;
  if (typeof text !== 'string' || text.length === 0) return null;
  return text;
}

function isTextPart(part: unknown): boolean {
  return (
    part != null &&
    typeof part === 'object' &&
    (part as { type?: unknown }).type === 'text'
  );
}

/**
 * Copia de `message` con todo su texto reemplazado por `text`: el primer text
 * part pasa a ser `{ type: 'text', text }` y los demás text parts se eliminan
 * (un documento partido entre parts no sobrevive). Los parts que no son texto
 * quedan como estaban.
 */
export function replaceUserMessageText(message: UIMessage, text: string): UIMessage {
  const parts = Array.isArray(message.parts) ? message.parts : [];
  const next: UIMessage['parts'] = [];
  let replaced = false;
  for (const part of parts) {
    if (!isTextPart(part)) {
      next.push(part);
      continue;
    }
    if (replaced) continue;
    replaced = true;
    next.push({ type: 'text', text } as UIMessage['parts'][number]);
  }
  if (!replaced) {
    next.unshift({ type: 'text', text } as UIMessage['parts'][number]);
  }
  return { ...message, parts: next };
}

export interface RedactLastUserMessageResult {
  /** La lista con el mensaje reemplazado (la misma referencia si no había). */
  messages: UIMessage[];
  /** Id del mensaje reemplazado, o `null` si no había mensaje del usuario. */
  messageId: string | null;
}

/**
 * Reemplaza el texto del ÚLTIMO mensaje del usuario por `text`.
 *
 * Es el mensaje del turno en curso: `useChat` manda un turno a la vez y el
 * aviso llega antes que cualquier contenido del assistant (que, si ya empezó
 * a llegar, se salta). No muta la entrada.
 */
export function redactLastUserMessage(
  messages: UIMessage[],
  text: string,
): RedactLastUserMessageResult {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role !== 'user') continue;
    const next = [...messages];
    next[i] = replaceUserMessageText(message, text);
    return { messages: next, messageId: message.id };
  }
  return { messages, messageId: null };
}
